import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, alpha } from '@mui/material';

/**
 * Generic drag-and-drop Kanban board.
 * @param {object[]} columns - { id, label, color, bg, border }
 * @param {object[]} items
 * @param {(item) => string|number} getItemId
 * @param {(item) => string} resolveColumnId
 * @param {(item, ctx) => React.ReactNode} renderCard - ctx: { isDragging, draggable }
 * @param {(item) => boolean} canDragItem
 * @param {(item, columnId) => void} onColumnChange
 * @param {string|number|null} updatingItemId
 * @param {string} dragMimeType
 * @param {string} emptyMessage
 * @param {(a, b) => number} [sortItems] - optional sort within each column
 */
const KanbanBoard = ({
  columns,
  items,
  getItemId,
  resolveColumnId,
  renderCard,
  canDragItem = () => true,
  onColumnChange,
  updatingItemId = null,
  dragMimeType = 'application/x-kanban-item-id',
  emptyMessage = 'Drop items here',
  sortItems,
}) => {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const boardColumns = useMemo(() => {
    const grouped = Object.fromEntries(columns.map((c) => [c.id, []]));
    items.forEach((item) => {
      const status = resolveColumnId(item);
      if (grouped[status]) {
        grouped[status].push(item);
      } else if (columns[0]) {
        grouped[columns[0].id].push(item);
      }
    });
    if (sortItems) {
      Object.keys(grouped).forEach((key) => {
        grouped[key].sort(sortItems);
      });
    }
    return columns.map((col) => ({
      ...col,
      items: grouped[col.id] || [],
    }));
  }, [columns, items, resolveColumnId, sortItems]);

  const handleDragStart = (e, item) => {
    if (!canDragItem(item)) {
      e.preventDefault();
      return;
    }
    const id = getItemId(item);
    setDraggingId(id);
    e.dataTransfer.setData(dragMimeType, String(id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDraggingId(null);
    const rawId = e.dataTransfer.getData(dragMimeType);
    if (!rawId) return;
    const item = items.find((i) => String(getItemId(i)) === String(rawId));
    if (!item || !canDragItem(item)) return;
    const current = resolveColumnId(item);
    if (current === columnId) return;
    onColumnChange(item, columnId);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(columns.length, 2)}, minmax(0, 1fr))`,
          md: `repeat(${Math.min(columns.length, 3)}, minmax(0, 1fr))`,
          lg: `repeat(${Math.min(columns.length, 4)}, minmax(0, 1fr))`,
          xl: `repeat(${columns.length}, minmax(0, 1fr))`,
        },
        gap: 2,
        alignItems: 'start',
      }}
    >
      {boardColumns.map((column) => {
        const isDropTarget = dragOverColumn === column.id;
        return (
          <Box
            key={column.id}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={() => setDragOverColumn((prev) => (prev === column.id ? null : prev))}
            onDrop={(e) => handleDrop(e, column.id)}
            sx={{
              borderRadius: '14px',
              minHeight: 280,
              p: 1.5,
              background: isDropTarget
                ? `linear-gradient(180deg, ${column.bg} 0%, rgba(15,23,42,0.6) 100%)`
                : 'rgba(15,23,42,0.45)',
              border: `1px solid ${isDropTarget ? column.border : 'rgba(148,163,184,0.15)'}`,
              transition: 'border-color 0.2s ease, background 0.2s ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: column.color,
                    boxShadow: `0 0 8px ${column.color}`,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: '#fff',
                    letterSpacing: 0.5,
                  }}
                >
                  {column.label}
                </Typography>
              </Box>
              <Chip
                label={column.items.length}
                size="small"
                sx={{
                  height: 22,
                  minWidth: 28,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: column.bg,
                  color: column.color,
                  border: `1px solid ${column.border}`,
                }}
              />
            </Box>

            <Box sx={{ minHeight: 120 }}>
              {column.items.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: 'rgba(255,255,255,0.35)',
                    textAlign: 'center',
                    py: 4,
                    fontStyle: 'italic',
                  }}
                >
                  {emptyMessage}
                </Typography>
              ) : (
                column.items.map((item) => {
                  const id = getItemId(item);
                  const canDrag = canDragItem(item);
                  const isUpdating = updatingItemId != null && String(updatingItemId) === String(id);
                  return (
                    <Box
                      key={id}
                      draggable={canDrag && !isUpdating}
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={handleDragEnd}
                      sx={{ opacity: isUpdating ? 0.6 : 1, pointerEvents: isUpdating ? 'none' : 'auto' }}
                    >
                      {renderCard(item, {
                        isDragging: draggingId != null && String(draggingId) === String(id),
                        draggable: canDrag,
                      })}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default KanbanBoard;
